import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Phone, Target, Zap, BarChart3, Users, Repeat } from 'lucide-react';
import { formatMarketingFigures } from '@/config/marketing';

function getServices(t: (key: string, options?: Record<string, string>) => string, firstMeeting: string) {
    return [
        {
            icon: Phone,
            title: t('admin:landing.services.items.coldCalling.title'),
            description: t('admin:landing.services.items.coldCalling.description'),
            color: 'from-purple-500 to-fuchsia-500'
        },
        {
            icon: Target,
            title: t('admin:landing.services.items.targeting.title'),
            description: t('admin:landing.services.items.targeting.description'),
            color: 'from-blue-500 to-cyan-500'
        },
        {
            icon: Users,
            title: t('admin:landing.services.items.team.title'),
            description: t('admin:landing.services.items.team.description'),
            color: 'from-pink-500 to-rose-500'
        },
        {
            icon: Repeat,
            title: t('admin:landing.services.items.iteration.title'),
            description: t('admin:landing.services.items.iteration.description'),
            color: 'from-violet-500 to-purple-500'
        },
        {
            icon: BarChart3,
            title: t('admin:landing.services.items.data.title'),
            description: t('admin:landing.services.items.data.description'),
            color: 'from-green-500 to-emerald-500'
        },
        {
            icon: Zap,
            title: t('admin:landing.services.items.speed.title'),
            description: t('admin:landing.services.items.speed.description', { firstMeeting }),
            color: 'from-orange-500 to-yellow-500'
        },
    ];
}

export default function ServicesSection() {
    const { t, i18n } = useTranslation(['admin']);
    const services = getServices(t, formatMarketingFigures(i18n.language).firstMeeting);
    return (
        <section id="services" className="relative py-32 bg-[#0a0a0a]">
            <div className="max-w-6xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <span className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-4 block">
                        {t('admin:landing.services.label')}
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold text-white">
                        {t('admin:landing.services.heading')}
                    </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {services.map((service, index) => (
                        <motion.div
                            key={service.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.08 }}
                            className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04] hover:border-white/10 transition-all duration-300"
                        >
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                <service.icon className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                            <p className="text-sm text-white/40 leading-relaxed">{service.description}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}